import { QRCodeSVG } from "qrcode.react";
import { ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { FaSyncAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { SupervisorContext } from "../../../contexts/admin/Supervisor.context";
import { SocketContext } from "../../../contexts/global/SocketContext";
import {
    WhatsappSession,
    WhatsappSessionAction,
    WhatsappSessionsResponse,
} from "../../../interfaces/WhatsappSessions.interfaces";
import { customRequest } from "../../../services/api";
import { ConfirmSessionAction } from "./ConfirmSessionAction";
import { SessionCard } from "./SessionCard";
import { formatTime, getSessionState } from "./sessionFormat";
import { StyledNumerosWhatsapp } from "./style";

// Position of the panel in the supervisor menu (SupervisorContext titles use the same index).
export const NUMEROS_WHATSAPP_MENU = 8;

const POLL_INTERVAL_MS = 15_000;

// Events that change a session: QR emitted, pairing done, init/ready/actions relayed by the backend.
const SESSION_EVENTS = ["qr", "authenticated", "whatsapp-sessions-update"];

const UNSUPPORTED_MESSAGES: Record<NonNullable<WhatsappSessionsResponse["reason"]>, string> = {
    OFFICIAL: "Este cliente usa a API oficial do WhatsApp, então não há sessões para gerenciar aqui.",
    WWEBJS_API: "Os números deste cliente são gerenciados pela nova plataforma (wwebjs-api), não pelo wa-client-lite.",
    NOT_CONFIGURED: "O endereço do wa-client-lite (parâmetro wwebjs_backend_url) não está configurado para este cliente.",
};

function getErrorMessage(err: any, fallback: string): string {
    const status = err?.response?.status;

    if (status === 401) return "Sua sessão expirou. Entre novamente no sistema.";
    if (status === 403) return "Apenas supervisores podem gerenciar os números do WhatsApp.";

    const message = err?.response?.data?.message;

    if (Array.isArray(message)) return message.join(" ");
    if (typeof message === "string" && message) return message;
    if (err?.code === "ECONNABORTED") return "O servidor demorou demais para responder.";

    return fallback;
};

function useNow(intervalMs: number) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);

    return now;
};

export const NumerosWhatsapp = () => {
    const { socket, pendingQrCodes } = useContext(SocketContext);
    const { modalState } = useContext(SupervisorContext);

    const [sessions, setSessions] = useState<WhatsappSessionsResponse | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [pendingActions, setPendingActions] = useState<Record<string, WhatsappSessionAction>>({});
    const lastRequestRef = useRef(0);
    const now = useNow(1000);

    const loadSessions = useCallback(() => {
        // Polls and socket refreshes overlap; only the newest answer is applied.
        const requestId = ++lastRequestRef.current;
        setIsLoading(true);

        return customRequest<WhatsappSessionsResponse, undefined>({
            method: "get",
            endpoint: "/whatsapp-sessions",
            timeoutSeconds: 20,
            onSuccess: (data) => {
                if (requestId !== lastRequestRef.current) return;

                setSessions(data);
                setUpdatedAt(new Date().toISOString());
                setError(null);
            },
            onError: (err) => {
                if (requestId !== lastRequestRef.current) return;

                setError(getErrorMessage(err, "Não foi possível carregar os números do WhatsApp."));
            },
        }).finally(() => {
            if (requestId === lastRequestRef.current) setIsLoading(false);
        });
    }, []);

    useEffect(() => {
        loadSessions();

        const timer = setInterval(loadSessions, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [loadSessions]);

    useEffect(() => {
        const currentSocket = socket.value;

        if (!currentSocket) return;

        let refreshTimer: ReturnType<typeof setTimeout> | null = null;
        const scheduleRefresh = () => {
            if (refreshTimer) clearTimeout(refreshTimer);
            refreshTimer = setTimeout(loadSessions, 500);
        };

        SESSION_EVENTS.forEach((event) => currentSocket.on(event, scheduleRefresh));

        return () => {
            SESSION_EVENTS.forEach((event) => currentSocket.off(event, scheduleRefresh));
            if (refreshTimer) clearTimeout(refreshTimer);
        };
    }, [socket.value, loadSessions]);

    const runAction = (session: WhatsappSession, action: WhatsappSessionAction) => {
        const { number } = session;
        setPendingActions((current) => ({ ...current, [number]: action }));

        customRequest<WhatsappSession, Record<string, never>>({
            method: "post",
            endpoint: `/whatsapp-sessions/${encodeURIComponent(number)}/${action === "RESTART" ? "restart" : "logout"}`,
            requestData: {},
            timeoutSeconds: 60,
            onSuccess: (updated) => {
                toast.success(
                    action === "RESTART"
                        ? "Sessão reiniciada. Reconectando ao WhatsApp…"
                        : "Número desvinculado. Leia o novo QR code para conectar."
                );
                setSessions((current) => current && {
                    ...current,
                    instances: current.instances.map((i) => (i.number === updated.number ? updated : i)),
                });
            },
            onError: (err) => {
                toast.error(getErrorMessage(err, "Não foi possível concluir a ação no WhatsApp."));
            },
        }).finally(() => {
            setPendingActions(({ [number]: _, ...rest }) => rest);
            loadSessions();
        });
    };

    const confirmAction = (session: WhatsappSession, action: WhatsappSessionAction) => {
        modalState.set(
            <ConfirmSessionAction
                session={session}
                action={action}
                onCancel={() => modalState.reset()}
                onConfirm={() => {
                    modalState.reset();
                    runAction(session, action);
                }}
            />
        );
    };

    const instances = sessions?.instances || [];
    const connectedCount = instances.filter((s) => getSessionState(s) === "CONNECTED").length;

    let content: ReactNode;

    if (!sessions && error) {
        content = (
            <div className="notice error">
                {error}
                <br />
                <button type="button" onClick={loadSessions}>Tentar novamente</button>
            </div>
        );
    } else if (!sessions) {
        content = <div className="notice">Carregando números…</div>;
    } else if (sessions.reason === "WWEBJS_API" && pendingQrCodes.length > 0) {
        // wwebjs-api has no number list here, but its QR codes still reach this screen.
        content = (
            <>
                <div className="notice">{UNSUPPORTED_MESSAGES.WWEBJS_API} O QR code recebido aparece abaixo.</div>
                {pendingQrCodes.map((pending) => (
                    <div className="notice qr" key={pending.number || "unknown"}>
                        <div role="img" aria-label="QR code de pareamento">
                            <QRCodeSVG value={pending.qr} size={168} />
                        </div>
                        <p>
                            {pending.number ? `Número ${pending.number}: ` : ""}
                            no celular, abra o WhatsApp em <b>Dispositivos conectados</b> e leia este QR code.
                            Gerado às {formatTime(new Date(pending.receivedAt).toISOString())}.
                        </p>
                    </div>
                ))}
            </>
        );
    } else if (!sessions.supported && sessions.reason) {
        content = <div className="notice">{UNSUPPORTED_MESSAGES[sessions.reason]}</div>;
    } else if (instances.length === 0) {
        content = (
            <div className="notice">
                O wa-client-lite não retornou nenhum número para este cliente. Confira se as instâncias
                estão ativas e se a versão instalada já tem o gerenciamento de sessões.
            </div>
        );
    } else {
        content = (
            <>
                {error && (
                    <div className="notice error">
                        Falha ao atualizar: {error} Exibindo os dados das {formatTime(updatedAt)}.
                    </div>
                )}
                <div className="sessions">
                    {instances.map((session) => (
                        <SessionCard
                            key={session.number}
                            session={session}
                            now={now}
                            pendingAction={pendingActions[session.number]}
                            socketQr={pendingQrCodes.find((pending) => pending.number === session.number)}
                            onRestart={() => confirmAction(session, "RESTART")}
                            onLogout={() => confirmAction(session, "LOGOUT")}
                        />
                    ))}
                </div>
            </>
        );
    }

    return (
        <StyledNumerosWhatsapp>
            <header>
                <div>
                    <h2>Números do WhatsApp</h2>
                    <p>
                        {instances.length > 0 && `${connectedCount} de ${instances.length} conectado${instances.length > 1 ? "s" : ""} · `}
                        {updatedAt
                            ? `Atualizado às ${formatTime(updatedAt)}, atualização automática a cada ${POLL_INTERVAL_MS / 1000} s`
                            : "Status das sessões atendidas pelo wa-client-lite"}
                    </p>
                </div>
                <button type="button" onClick={loadSessions} disabled={isLoading}>
                    <FaSyncAlt />
                    Atualizar
                </button>
            </header>
            {content}
        </StyledNumerosWhatsapp>
    );
};
